import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../config/api'
import { fetchSectionsWithVersion, saveSectionsWithVersion } from '../utils/sectionsClient'

interface WpsSectionsContextValue {
  /** All sections data for this pipe set. */
  sections: Record<string, any>
  /** Optimistic-concurrency version for `wps_sections`. */
  version: number
  /** Whether sections are currently being loaded from the API. */
  loading: boolean
  /** Error message if loading or saving failed. */
  error: string | null
  /** Read a specific section (e.g., 'base', 'bevel', 'pass'). */
  getSection: (key: string) => any
  /** Update a specific section locally and persist to the API. */
  updateSection: (key: string, data: any) => Promise<void>
  /** Force reload sections from the API. Returns the fetched blob for immediate use. */
  refresh: () => Promise<{ sections: Record<string, any>; version: number }>
  /** Bump the optimistic-concurrency token after a server-side sections write. */
  syncSectionsVersion: (version: number) => void
}

const WpsSectionsContext = createContext<WpsSectionsContextValue | undefined>(undefined)

interface WpsSectionsProviderProps {
  projectId: string
  pipeSetId: string
  children: React.ReactNode
}

export function WpsSectionsProvider({ projectId, pipeSetId, children }: WpsSectionsProviderProps) {
  const [sections, setSections] = useState<Record<string, any>>({})
  const [version, setVersion] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Keep refs to the latest sections/version for save operations. Update
  // them synchronously inside updateSection/runFetch — not only on render —
  // so back-to-back awaits (floorsheet then weldparams) see the bumped
  // If-Match version and merged blob without waiting for React to commit.
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections
  const versionRef = useRef(version)
  versionRef.current = version
  // Serialize PUTs so overlapping updateSection calls cannot race on the
  // same If-Match (self-409). Errors from one save must not block the queue.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())

  /**
   * Internal fetch helper. The `silent` flag controls whether we flip the
   * shared `loading` state on/off around the request:
   *
   *   - `silent: false` (initial mount) — flip `loading` so the surrounding
   *     `PipeSetSectionsOutlet` shows its `<PageLoader/>` until the first
   *     fetch resolves.
   *   - `silent: true` (post-mount refreshes) — leave `loading` alone. The
   *     outlet's `if (loading) return <PageLoader/>` branch would otherwise
   *     unmount `<Outlet/>` (and every page-local state inside it) for the
   *     duration of the refresh, then remount with fresh state. This blew
   *     away the `viewer` state in `PqrPreviewPage` after a successful
   *     "Save & Generate Report" so the freshly-generated PDF viewer
   *     never appeared. Keeping refresh quiet preserves page-local state
   *     across the in-flight refetch.
   */
  const runFetch = useCallback(
    async (silent: boolean): Promise<{ sections: Record<string, any>; version: number }> => {
      try {
        if (!silent) setLoading(true)
        setError(null)
        const loaded = await fetchSectionsWithVersion(projectId, pipeSetId)
        const nextSections = loaded.sections || {}
        sectionsRef.current = nextSections
        versionRef.current = loaded.version
        setSections(nextSections)
        setVersion(loaded.version)
        return { sections: nextSections, version: loaded.version }
      } catch (err: any) {
        console.error('Failed to fetch sections:', err)
        setError(err.message || 'Failed to load sections')
        return { sections: sectionsRef.current, version: versionRef.current }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [projectId, pipeSetId]
  )

  // Silent refresh exposed to consumers — see runFetch's docstring for why
  // post-mount refreshes must NOT flip `loading`.
  const refresh = useCallback(
    () => runFetch(true),
    [runFetch]
  )

  const syncSectionsVersion = useCallback((nextVersion: number) => {
    if (!Number.isFinite(nextVersion) || nextVersion <= 0) return
    versionRef.current = nextVersion
    setVersion(nextVersion)
  }, [])

  useEffect(() => {
    void runFetch(false)
  }, [runFetch])

  const getSection = useCallback(
    (key: string) => sections[key] ?? null,
    [sections]
  )

  const updateSection = useCallback(async (key: string, data: any): Promise<void> => {
    const run = async (): Promise<void> => {
      const updated = { ...sectionsRef.current, [key]: data }
      sectionsRef.current = updated
      setSections(updated)
      setError(null)

      try {
        const expected =
          versionRef.current > 0 ? versionRef.current : null
        const saved = await saveSectionsWithVersion(
          projectId,
          pipeSetId,
          updated,
          expected
        )
        sectionsRef.current = saved.sections
        versionRef.current = saved.version
        setSections(saved.sections)
        setVersion(saved.version)
      } catch (err: any) {
        console.error(`Failed to save section "${key}":`, err)
        if (err instanceof ApiError && err.status === 409) {
          await runFetch(true)
          const conflict =
            'This section was updated elsewhere. Reloaded the latest data — review your changes and save again.'
          setError(conflict)
          throw new ApiError(409, conflict)
        }
        const message =
          err instanceof ApiError && err.status === 401
            ? 'Not authenticated — sign in again and retry Save.'
            : err?.message || 'Failed to save'
        setError(message)
        throw err instanceof ApiError ? new ApiError(err.status, message) : err
      }
    }

    const queued = saveChainRef.current.then(run, run)
    // Keep the chain alive after failures so later saves still serialize.
    saveChainRef.current = queued.then(
      () => undefined,
      () => undefined
    )
    return queued
  }, [projectId, pipeSetId, runFetch])

  const value = useMemo(
    () => ({
      sections,
      version,
      loading,
      error,
      getSection,
      updateSection,
      refresh,
      syncSectionsVersion
    }),
    [sections, version, loading, error, getSection, updateSection, refresh, syncSectionsVersion]
  )

  return (
    <WpsSectionsContext.Provider value={value}>
      {children}
    </WpsSectionsContext.Provider>
  )
}

export function useWpsSections() {
  const context = useContext(WpsSectionsContext)
  if (!context) {
    throw new Error('useWpsSections must be used within WpsSectionsProvider')
  }
  return context
}

/**
 * Non-throwing variant of `useWpsSections`. Returns `null` when called
 * outside a `<WpsSectionsProvider>`. Use this from components that *can*
 * render either inside the pipe-set layout (where the provider is
 * mounted) or standalone in tests / other surfaces — so the consumer
 * can opt-in to refreshing the shared cache when the provider is
 * available, without forcing every test harness to mount one.
 */
export function useOptionalWpsSections(): WpsSectionsContextValue | null {
  const context = useContext(WpsSectionsContext)
  return context ?? null
}
